import {Map as ImMap, Set as ImSet, List, fromJS} from 'immutable';
import {compose} from '@packages/utilities/fp/utils';
import {getId, indexById} from '@packages/systems/core/utils/RecordUtils';
import {
  reifyCollection,
  getSortPos,
  reifyCollections,
} from '@packages/systems/dynamo/utils/CollectionUtils';
import {
  add,
  remove,
  merge,
  mergeDeep,
} from '@packages/systems/core/utils/ImmUtils';
import {findPivot} from '@packages/systems/core/utils/NumberUtils';
import {
  type ScheduleItemsType,
  type CollectionStateType,
  CollectionState,
  LocalizedItemCountByLocaleType,
} from '@packages/systems/core/records/CollectionStateType';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {mergeLocalizedItemCountByLocaleId} from '@packages/systems/localization';
import type {Locale} from '@packages/systems/localization-types';

import {
  BULK_ITEM_DELETION_COMPLETED_REMOTELY,
  CANVAS_DYN_ITEM_SAVED,
  COLLECTIONS_RECEIVED,
  COLLECTION_ITEM_COUNTS_RECEIVED,
  COLLECTION_REMOVED_REMOTELY,
  COMMERCE_ITEM_CREATION_RECOVERY_STARTED,
  DYN_COLLECTION_DELETED,
  DYN_COLLECTION_MINTED,
  DYN_COLLECTION_DUPLICATED,
  DYN_COLLECTION_SAVED,
  DYN_INLINE_ITEM_MINTED,
  DYN_ITEM_DELETED,
  DYN_ITEM_MINTED,
  DYN_ITEM_SAVED,
  DYN_ITEM_SAVED_AND_PUBLISHED,
  DYN_ITEM_UNSCHEDULE_SUCCEEDED,
  ITEM_DUPLICATED,
  ITEM_REMOVED_REMOTELY,
  RAW_COLLECTION_RECEIVED,
  SCHEDULED_ITEMS_RECEIVED,
  RAW_ITEMS_RECEIVED,
  CSV_ITEMS_GENERATED,
  BULK_ITEM_DELETION_COMPLETED,
  BULK_ITEM_DRAFTING_COMPLETED,
  BULK_ITEM_ARCHIVING_COMPLETED,
  COMMERCE_CONNECT_SUCCEEDED,
  CMS_COLLECTION_SORT_ENDED,
  COMMERCE_STATUS_FETCH,
  BULK_ITEM_UNPUBLISH_COMPLETED,
  SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED,
} from '@packages/systems/designer/actions';

const emptySet = ImSet<string>();

import {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {addInventoryFieldToSkuCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {CollectionAction} from './actions';
import {FailedItems} from '@packages/systems/core/records/BulkItemEditingState';

export const initialState = CollectionState();

export default function CollectionStore(
  state: CollectionStateType,
  action: CollectionAction,
  {currentLocale}: {currentLocale?: Locale}
) {
  switch (action.type) {
    case COLLECTION_ITEM_COUNTS_RECEIVED: {
      const {payload} = action;
      let nextState = state.set(
        '__DEPRECATED__itemCountByCollectionId',
        payload.itemCountByCollectionId
      );

      if (payload.localizedItemCountByLocaleId) {
        nextState = nextState.updateIn<LocalizedItemCountByLocaleType>(
          ['localizedItemCountByLocaleId'],
          (localizedItemCountByLocaleId) => {
            return payload.localizedItemCountByLocaleId
              ? mergeLocalizedItemCountByLocaleId(
                  localizedItemCountByLocaleId,
                  payload.localizedItemCountByLocaleId
                )
              : localizedItemCountByLocaleId;
          }
        );
      }

      return nextState;
    }

    case COLLECTIONS_RECEIVED: {
      const {collections} = action.payload;
      return state.set('collections', collections);
    }

    case DYN_COLLECTION_DELETED: {
      const {collection} = action.payload;
      return state.removeIn(['collections', collection._id]);
      // TODO: Delete Items
    }

    case COLLECTION_REMOVED_REMOTELY: {
      const {collectionId} = action.payload;
      return state
        .removeIn(['collections', collectionId])
        .removeIn(['itemIndex', collectionId]);
    }

    case DYN_COLLECTION_MINTED:
    case DYN_COLLECTION_DUPLICATED: {
      const {collection} = action.payload;
      return state.setIn(['collections', collection._id], collection);
    }

    case DYN_COLLECTION_SAVED: {
      const {collection, oldCollectionId} = action.payload;

      const id = getId(collection);
      const items = ImMap(
        state.itemIndex.get(oldCollectionId, emptySet).entries()
        // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
      ).map((itemId) => state.items.get(itemId));

      return replaceIn(
        state,
        ['collections', oldCollectionId],
        [id, collection]
      )
        .update('collections', addInventoryFieldToSkuCollection)
        .updateIn(['itemIndex', id], ImSet)
        .update('items', merge(items));
    }

    case RAW_ITEMS_RECEIVED: {
      const {
        payload: {items, mergeExistingItemsTogether},
      } = action;
      return addItemsToStore({
        items,
        state,
        mergeExistingItemsTogether,
        currentLocale,
      });
    }

    case SCHEDULED_ITEMS_RECEIVED: {
      const {scheduledItems} = action.payload;
      return state.set('scheduledItems', scheduledItems);
    }

    case CSV_ITEMS_GENERATED: {
      const {items, collection, hasNewFields} = action.payload;
      if (hasNewFields) {
        state = state.setIn(['collections', collection._id], collection);
      }

      return addItemsToStore({
        items: items.toArray(),
        state,
        mergeExistingItemsTogether: false,
        currentLocale,
      });
    }

    case RAW_COLLECTION_RECEIVED: {
      const {response} = action.payload;

      const collection = reifyCollection(response);
      return state
        .setIn(['collections', collection._id], collection)
        .update('collections', addInventoryFieldToSkuCollection);
    }

    case DYN_ITEM_DELETED: {
      const {collectionId, item, unscheduled, locales} = action.payload;
      return removeItemFromStore({
        collectionId,
        item,
        state,
        unscheduled,
        locales,
      });
    }

    case ITEM_REMOVED_REMOTELY: {
      const {itemId} = action.payload;

      return state
        .removeIn(['items', itemId])
        .updateIn<
          ImSet<string>
        >(['itemIndex', findParentCollectionId(state, itemId)], (s) => (s ? s.remove(itemId) : s));
    }

    case DYN_ITEM_UNSCHEDULE_SUCCEEDED: {
      const {request} = action.payload;

      if (!request || !request.meta) {
        return state;
      }
      const unscheduledItem = request.meta.get('item');
      return state.update(
        'scheduledItems',
        removeScheduledItem(unscheduledItem)
      );
    }

    case BULK_ITEM_DRAFTING_COMPLETED: {
      const {draftedItems, unscheduledItems} = action.payload;
      return state
        .update('items', (items) =>
          draftedItems.reduce(
            (newItems, draftItem) => newItems.set(getId(draftItem), draftItem),
            items
          )
        )
        .update('scheduledItems', removeScheduledItems(unscheduledItems));
    }

    case BULK_ITEM_ARCHIVING_COMPLETED: {
      const {archivedItems, unscheduledItems} = action.payload;

      return state
        .update('items', (items) =>
          archivedItems.reduce(
            (newItems, archivedItem) =>
              newItems.set(getId(archivedItem), archivedItem),
            items
          )
        )
        .update('scheduledItems', removeScheduledItems(unscheduledItems));
    }

    case BULK_ITEM_DELETION_COMPLETED:
    case BULK_ITEM_DELETION_COMPLETED_REMOTELY: {
      const {payload} = action;
      return bulkRemoveItems({state, ...payload});
    }

    case BULK_ITEM_UNPUBLISH_COMPLETED: {
      const {unpublishedItemIds, locales} = action.payload;

      let updatedState = state;

      locales.forEach((locale: Locale) => {
        updatedState = updatedState.update(
          'localizedItems',
          (localizedItems) => {
            let updatedLocalizedItems = localizedItems;

            unpublishedItemIds.forEach((itemId: string) => {
              updatedLocalizedItems = updatedLocalizedItems.updateIn(
                [locale.cmsId, itemId],
                undefined,
                (item) => {
                  return item
                    ? item
                        .set('published-on', undefined)
                        .set('published-by', undefined)
                        .set('_draft', true)
                    : undefined;
                }
              );
            });

            return updatedLocalizedItems;
          }
        );
      });

      return updatedState;
    }

    case DYN_ITEM_SAVED_AND_PUBLISHED:
    case DYN_ITEM_SAVED: {
      const {
        payload: {item, oldItemId, collectionId, unscheduled},
      } = action;

      const itemId = getId(item);
      const localeId = item.get('_locale');

      if (currentLocale && localeId) {
        const {secondaryItems} = action.payload;
        let itemsToUnschedule: List<ItemRecordType> = List(
          unscheduled ? [item] : []
        );

        if (secondaryItems && secondaryItems.length > 0) {
          itemsToUnschedule = itemsToUnschedule.concat(
            secondaryItems.reduce<ItemRecordType[]>((next, secondaryItem) => {
              if (secondaryItem.unscheduled) {
                next.push(secondaryItem.item);
              }
              return next;
            }, [])
          );
        }

        return state
          .removeIn(['localizedItems', localeId, oldItemId])
          .setIn(['localizedItems', localeId, itemId], item)
          .update('localizedItems', (localizedItems) => {
            if (secondaryItems && secondaryItems.length > 0) {
              secondaryItems.forEach(({item: secondaryItem}) => {
                const secondaryItemId = getId(secondaryItem);
                const secondaryLocaleId = secondaryItem.get('_locale');

                localizedItems = localizedItems
                  .removeIn([secondaryLocaleId, secondaryItemId])
                  .setIn([secondaryLocaleId, secondaryItemId], secondaryItem);
              });
            }
            return localizedItems;
          })
          .update('scheduledItems', (scheduledItems) =>
            itemsToUnschedule.size > 0
              ? removeScheduledItems(itemsToUnschedule)(scheduledItems)
              : scheduledItems
          );
      }

      return state
        .removeIn(['items', oldItemId])
        .setIn(['items', itemId], item)
        .updateIn(['itemIndex', collectionId], emptySet, (s) =>
          s.remove(oldItemId).add(itemId)
        )
        .update('scheduledItems', (scheduledItems) =>
          unscheduled
            ? removeScheduledItem(item)(scheduledItems)
            : scheduledItems
        );
    }

    case CANVAS_DYN_ITEM_SAVED: {
      const {itemId, fields} = action.payload;
      const item = fromJS(fields);
      const localeId = item.get('_locale');

      if (currentLocale && localeId) {
        return state.setIn(['localizedItems', localeId, itemId], item);
      }

      return state.setIn(['items', itemId], item);
    }

    case DYN_ITEM_MINTED:
    case ITEM_DUPLICATED: {
      const {
        payload: {item, collectionId},
      } = action;
      return stateWithMintItem({
        item,
        state,
        collectionId,
        currentLocale,
      });
    }

    case DYN_INLINE_ITEM_MINTED: {
      const {
        payload: {item, collectionId},
      } = action;
      return stateWithMintItem({
        item,
        state,
        collectionId,
        currentLocale,
      });
    }

    case COMMERCE_ITEM_CREATION_RECOVERY_STARTED: {
      const {collection, item, failedItem} = action.payload;
      const failedItemId = getId(failedItem);
      const collectionId = getId(collection);

      // first, remove the product that was created but became corrupted
      // since SKU creation failed
      const stateWithoutFailedItem = state
        .removeIn(['items', failedItemId])
        .updateIn<
          ImSet<string>
        >(['itemIndex', collectionId], (indexSet) => (indexSet ? indexSet.remove(failedItemId) : indexSet));

      // second, recreate the state with the recovered product data
      return stateWithMintItem({
        item,
        state: stateWithoutFailedItem,
        collectionId,
      });
    }

    case COMMERCE_CONNECT_SUCCEEDED: {
      const {response} = action.payload;
      const commerceCollectionsById = indexById<CollectionRecordType>(
        response.get('collections').toJS().map(reifyCollection)
      );

      return state
        .update('collections', (currentCollections) =>
          currentCollections.merge(commerceCollectionsById)
        )
        .update('collections', addInventoryFieldToSkuCollection);
    }

    case COMMERCE_STATUS_FETCH: {
      const {
        payload: {response},
      } = action;

      if (response && response.get('ok')) {
        const respCollections = response.get('collections').toJS();
        const commerceCollectionsById = compose(
          addInventoryFieldToSkuCollection
        )(reifyCollections)(respCollections);

        // update the ecommerce collections that have been upgraded
        return state.update('collections', (currentCollections) =>
          currentCollections.merge(commerceCollectionsById)
        );
      }
      return state;
    }

    case CMS_COLLECTION_SORT_ENDED: {
      const {collectionId, targetIndex, collections, previousIndex} =
        action.payload;

      let sortPos;

      if (targetIndex === collections.length - 1) {
        const lastCollection = collections[collections.length - 1];
        // moving it to the end of the list
        if (lastCollection) {
          sortPos = findPivot(
            Number.MAX_SAFE_INTEGER,
            getSortPos(state.collections.get(lastCollection))
          );
        }
      } else if (targetIndex === 0) {
        const firstCollection = collections[0];
        // moving it to the beginning of the list
        if (firstCollection) {
          sortPos = findPivot(
            getSortPos(state.collections.get(firstCollection)),
            0
          );
        }
      } else if (targetIndex < previousIndex) {
        const prevColl = collections[targetIndex - 1];
        const nextColl = collections[targetIndex];
        // moving it "up" the list
        if (prevColl && nextColl) {
          const prev = state.collections.get(prevColl);
          const next = state.collections.get(nextColl);
          if (!prev || !next) {
            return state;
          }
          sortPos = findPivot(getSortPos(next), getSortPos(prev));
        }
      } else if (targetIndex > previousIndex) {
        const prevColl = collections[targetIndex];
        const nextColl = collections[targetIndex + 1];
        // moving it "down" the list
        if (prevColl && nextColl) {
          const prev = state.collections.get(prevColl);
          const next = state.collections.get(nextColl);
          if (!prev || !next) {
            return state;
          }
          sortPos = findPivot(getSortPos(next), getSortPos(prev));
        }
      }

      return state.setIn(['collections', collectionId, 'sortPos'], sortPos);
    }

    case SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED: {
      const {productsCreatedCount, skusCreatedCount, collectionId} =
        action.payload;

      if (!collectionId || (!productsCreatedCount && !skusCreatedCount)) {
        return state;
      }

      let nextState = state;

      if (productsCreatedCount && collectionId.product) {
        nextState = nextState.updateIn(
          ['__DEPRECATED__itemCountByCollectionId', collectionId.product],
          0,
          (count) => (count || 0) + productsCreatedCount
        );

        if (currentLocale && currentLocale.cmsId) {
          nextState = nextState.updateIn(
            [
              'localizedItemCountByLocaleId',
              currentLocale.cmsId,
              collectionId.product,
            ],
            0,
            (count) => (count || 0) + productsCreatedCount
          );
        }
      }

      if (skusCreatedCount && collectionId.sku) {
        nextState = nextState.updateIn(
          ['__DEPRECATED__itemCountByCollectionId', collectionId.sku],
          0,
          (count) => (count || 0) + skusCreatedCount
        );

        if (currentLocale && currentLocale.cmsId) {
          nextState = nextState.updateIn(
            [
              'localizedItemCountByLocaleId',
              currentLocale.cmsId,
              collectionId.sku,
            ],
            0,
            (count) => (count || 0) + skusCreatedCount
          );
        }
      }

      return nextState;
    }

    default: {
      return state;
    }
  }
}

function replaceIn(
  im: CollectionStateType,
  _keyPath: Array<string>,
  entry: Array<CollectionRecordType | any>
) {
  const keyPath = _keyPath.slice(0, -1);
  const [key] = _keyPath.slice(-1);
  return im.updateIn(keyPath, (x) =>
    // @ts-expect-error - TS18046 - 'x' is of type 'unknown'.
    x.mapEntries(([k, v]: [any, any]) => (k === key ? entry : [k, v]))
  );
}

const stateWithMintItem = ({
  item,
  state,
  collectionId,
  currentLocale,
}: {
  item: ItemRecordType;
  state: CollectionStateType;
  collectionId: string;
  currentLocale?: Locale;
}) => {
  const id = getId(item);
  const localeId = item.get('_locale');

  if (localeId) {
    return addItemsToStore({
      items: [item],
      state,
      mergeExistingItemsTogether: false,
      currentLocale,
    });
  }

  return state
    .setIn(['items', id], item)
    .updateIn(['itemIndex', collectionId], emptySet, add(id));
};

const findParentCollectionId = (state: CollectionStateType, id: string) => {
  return state.itemIndex.findKey((s) => s.has(id)) || '';
};

/**
  If mergeExistingItemsTogether is truthy, then partial item responses (such as an `itemRef` response which only returns
  a subset of the item's fields) will be merged with item that's in the store if it's been loaded in a different manner, such as the full item request.
 */
const addItemsToStore = ({
  items: itemList,
  state,
  mergeExistingItemsTogether,
  currentLocale,
}: {
  items: ItemRecordType[];
  state: CollectionStateType;
  mergeExistingItemsTogether?: boolean;
  currentLocale?: Locale;
}) => {
  const index = ImMap<string, ImSet<string>>().asMutable();
  const hasLocaleId =
    currentLocale && itemList?.some((item) => item.get('_locale') ?? false);

  const items = hasLocaleId
    ? ImMap()
    : // mapper function `mapItem` is invoked for each item in the itemList.
      // Here, we're using it to set the index to avoid iterating over the collection twice
      indexById(
        itemList,
        function mapItem(item: ItemRecordType, itemId: string): ItemRecordType {
          const collectionId = item.get('_cid');

          if (collectionId) {
            index.set(
              collectionId,
              (index.get(collectionId) || ImSet()).add(itemId)
            );
          }

          if (mergeExistingItemsTogether) {
            const existingItem = state.items.get(itemId);

            if (existingItem) {
              return existingItem.merge(item);
            }
          }

          return item;
        }
      );

  const localizedItems = state.localizedItems.asMutable();

  if (hasLocaleId) {
    itemList?.forEach((item) => {
      const itemId = item.get('_id');
      const localeId = item.get('_locale');

      if (localeId) {
        if (mergeExistingItemsTogether) {
          const existingItem = state.localizedItems.getIn([localeId, itemId]);

          if (existingItem) {
            item = existingItem.merge(item);
          }
        }

        localizedItems.setIn([localeId, itemId], item);
      }
    });
  }

  return state
    .update('items', merge(items))
    .set('localizedItems', localizedItems.asImmutable())
    .update('itemIndex', mergeDeep(index.asImmutable()));
};

const removeItemFromStore = ({
  collectionId,
  item,
  state,
  unscheduled,
  locales,
}: {
  collectionId: string;
  item: ItemRecordType;
  state: CollectionStateType;
  unscheduled?: boolean;
  locales?: Locale[];
}) => {
  const itemId = getId(item);
  const localeId = item.get('_locale');

  if (localeId && Array.isArray(locales) && locales.length > 0) {
    let nextState = state
      .removeIn(['localizedItems', localeId, itemId])
      .update('scheduledItems', (scheduledItems) =>
        unscheduled ? removeScheduledItem(item)(scheduledItems) : scheduledItems
      );

    locales.forEach((locale) => {
      nextState = nextState.removeIn(['localizedItems', locale.cmsId, itemId]);
    });

    return nextState;
  }

  return state
    .removeIn(['items', itemId])
    .updateIn(['itemIndex', collectionId], emptySet, remove(itemId))
    .update('scheduledItems', (scheduledItems) =>
      unscheduled ? removeScheduledItem(item)(scheduledItems) : scheduledItems
    );
};

const bulkRemoveItems = ({
  state,
  collectionId,
  removedItemsIds,
  unscheduledItems,
  locales,
}: {
  state: CollectionStateType;
  collectionId: string;
  removedItemsIds: List<string>;
  locales: Locale[];
  unscheduledItems: List<ItemRecordType>;
  failedItems: FailedItems;
  completedAction: 'delete';
}) => {
  if (Array.isArray(locales) && locales.length > 0) {
    return state
      .update('localizedItems', (localizedItems) => {
        locales.forEach((locale) => {
          removedItemsIds.forEach((itemId) => {
            localizedItems = localizedItems.removeIn([locale.cmsId, itemId]);
          });
        });
        return localizedItems;
      })
      .update('scheduledItems', removeScheduledItems(unscheduledItems));
  }

  return state
    .update('items', (items) =>
      removedItemsIds.reduce(
        (newItems, itemId) => newItems.delete(itemId),
        items
      )
    )
    .updateIn(['itemIndex', collectionId], emptySet, (set) =>
      set.subtract(removedItemsIds)
    )
    .update('scheduledItems', removeScheduledItems(unscheduledItems));
};

const removeScheduledItems =
  (items?: List<ItemRecordType>) => (scheduledItems: ScheduleItemsType) =>
    items && items.size > 0
      ? scheduledItems.filter(
          (scheduledItem) =>
            !items.some((item) => {
              if (item.get('_locale') && scheduledItem.localeId) {
                return (
                  item.get('_id') === scheduledItem.itemId &&
                  item.get('_locale') === scheduledItem.localeId
                );
              }
              return item.get('_id') === scheduledItem.itemId;
            })
        )
      : scheduledItems;

const removeScheduledItem = (item: ItemRecordType) =>
  removeScheduledItems(List.of(item));
